import { type FormEvent, useLayoutEffect, useRef, useState } from 'react';
import { Button, Textarea } from '@roger/ui-components';
import {
  HISTORY_TYPES,
  type LiveSpan,
  type RedoRelation,
  type Snapshot,
  encodeSnapshot,
  outsideOk,
  resolveSplice,
  sameLive,
  scanMentions,
  sharedLive,
  startSnapshot,
  survives,
  transition,
} from './mentionEdits';

// A native beforeinput capture: the draft and selection before the browser
// edits. It is valid only for the immediately paired input with the same text;
// `gen` pairs it with that input, and its own timer drops an unconsumed capture
// so a canceled edit cannot be reused by a later action.
type Pending = {
  value: string;
  start: number;
  end: number;
  inputType: string;
  composing: boolean;
  gen: number;
};

// One composition session. It stays `ending` after compositionend until the
// final input or the deferred reconciliation settles it once, so a legacy
// compositionend-before-input ordering still resolves against the base anchor.
// `committed` holds the native evidence that the composition actually inserted
// text: compositionend carries the committed string, and a canceled
// composition ends with empty data. A committed equal-text replacement is its
// own native history step, so it must record; a canceled one must not.
type Composition = {
  base: Snapshot;
  start: number;
  end: number;
  ending: boolean;
  committed: boolean;
  gen: number;
};

/**
 * Inline editor for one of your own messages. It reuses the send bar's
 * composer box (EMR-2616), so the focus sign is the same one: the attribute
 * turns off the text area's chat outline, and the three
 * `has-[textarea:focus-visible]:` classes draw the border and the ring.
 *
 * Mention identity follows the person's real edits. A native `beforeinput`
 * capture records the draft and selection before each edit, and the paired
 * `input` verifies the exact splice against them. An occurrence the edit
 * touches is dropped for good; a span the edit does not touch keeps its id.
 * When the browser gives no range to verify, the survival is uncertain and the
 * name stays plain text rather than guessing an id.
 *
 * Every committed edit, including a settled composition and an equal-text
 * replacement, is appended to a local metadata journal, so a native undo/redo
 * can restore ids the browser's text history cannot. A history step only reads
 * positions it can reach: an undo keeps the union of the feasible predecessors
 * of every frontier candidate, and a redo returns only to a source its own
 * observed undo could have left. Spans every reachable candidate proves are
 * restored; conflicting spans stay plain, and an unmatched target leaves the
 * frontier explicitly unknown rather than reusing a stale origin.
 *
 * The caller owns the failure toast: `onSave` never rejects, and a 409 closes
 * the editor from the message. Enter saves, Esc cancels and prevents default,
 * so the chat keyboard listener never also minimizes the window.
 */
export function MessageEditor({
  body,
  touch,
  onSave,
  onCancel,
}: {
  body: string;
  touch: boolean;
  onSave: (body: string) => Promise<void>;
  onCancel: () => void;
}) {
  // Lazy: a body change or a save re-render must not rescan the original text.
  const [initial] = useState(() => scanMentions(body));
  const [snap, setSnap] = useState<Snapshot>(() => startSnapshot(initial));
  const [saving, setSaving] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const snapRef = useRef(snap);
  snapRef.current = snap;
  const pendingRef = useRef<Pending | null>(null);
  const pendingGenRef = useRef(0);
  const compositionRef = useRef<Composition | null>(null);
  const composeGenRef = useRef(0);
  const consumedRef = useRef<Event | null>(null);
  // Every committed edit is a journal entry, so a native undo/redo can put the
  // metadata back. It is not a text undo stack: the browser owns the text.
  const journalRef = useRef<Snapshot[] | null>(null);
  if (journalRef.current === null) journalRef.current = [snap];
  // Positions in the journal the current visible text may be at. A grouped
  // native undo can leave text indistinguishable from several entries, so this
  // is a frontier of candidates, not one index.
  const frontierRef = useRef<number[]>([0]);
  // Positions an observed undo left, so its matching redo can return to them.
  const redoRef = useRef<RedoRelation[]>([]);

  const occurrences = initial.occurrences;
  const labelAt = (key: number) => `@${occurrences[key].name}`;

  const publish = (next: Snapshot) => {
    snapRef.current = next;
    setSnap(next);
  };
  // Append one committed edit while the frontier is at the journal end, so
  // ordinary typing does not copy the whole prefix. Any entry above
  // max(frontier) is abandoned for every candidate, so a new edit truncates
  // there; an unknown frontier (empty) starts a fresh chain because no earlier
  // entry is provably reachable.
  const record = (next: Snapshot) => {
    const frontier = frontierRef.current;
    if (frontier.length === 0) {
      journalRef.current = [next];
    } else {
      const journal = journalRef.current as Snapshot[];
      const keepTo = Math.max(...frontier);
      if (keepTo < journal.length - 1) {
        journalRef.current = journal.slice(0, keepTo + 1);
      }
      (journalRef.current as Snapshot[]).push(next);
    }
    redoRef.current = [];
    frontierRef.current = [(journalRef.current as Snapshot[]).length - 1];
    publish(next);
  };

  // Restore metadata for a native history step from the positions that step can
  // reach. Spans every reachable candidate proves stay certified; conflicting
  // spans stay plain. An unmatched target leaves an explicitly unknown frontier
  // so a later step cannot reuse a stale origin for a different visible value.
  const settleHistory = (text: string, candidates: number[]) => {
    const journal = journalRef.current as Snapshot[];
    const reachable = candidates.filter(
      i => journal[i] && journal[i].text === text
    );
    let live: LiveSpan[] = [];
    if (reachable.length === 1) live = journal[reachable[0]].live;
    else if (reachable.length > 1)
      live = sharedLive(reachable.map(i => journal[i]));
    const safe = live.filter(s => survives(text, labelAt(s.key), s));
    frontierRef.current = reachable;
    publish({ text, live: safe });
  };

  const restoreHistory = (text: string, inputType: string) => {
    const journal = journalRef.current as Snapshot[];
    const origins = frontierRef.current;
    if (inputType === 'historyUndo') {
      // An undo only moves backward, so a target is any entry strictly before a
      // feasible origin. Keep the union of every origin's predecessors, and keep
      // the source/target pairs so a redo returns only to a source the observed
      // undo could have left.
      const relation: RedoRelation = new Map();
      for (const origin of origins) {
        for (let target = 0; target < origin; target++) {
          if (journal[target]?.text !== text) continue;
          const sources = relation.get(target);
          if (sources) sources.push(origin);
          else relation.set(target, [origin]);
        }
      }
      redoRef.current.push(relation);
      settleHistory(text, [...relation.keys()]);
    } else {
      // A redo returns only to a source its own undo was observed to leave, and
      // only when that source still explains the observed redo target.
      const relation = redoRef.current.pop();
      const targets = new Set<number>();
      for (const reachable of origins) {
        for (const source of relation?.get(reachable) ?? []) {
          if (journal[source]?.text === text) targets.add(source);
        }
      }
      settleHistory(text, [...targets]);
    }
  };

  // The composition passage is one base-anchored replacement, not a scan of
  // equal labels. It stays provisional while composing and is committed once
  // when the session settles.
  const compositionLive = (
    text: string,
    composition: Composition
  ): LiveSpan[] => {
    const base = composition.base.text;
    if (!outsideOk(base, text, composition.start, composition.end)) return [];
    const inserted = text.slice(
      composition.start,
      text.length - (base.length - composition.end)
    );
    return transition(
      text,
      composition.base.live,
      { a: composition.start, b: composition.end, inserted },
      labelAt
    );
  };

  // One settled composition edit: record it when it changed text or provenance
  // (an equal-text provenance change counts), or when the composition committed
  // real input. A committed equal-text replacement is still its own native
  // history step, so it needs a journal entry for a later undo to reach; a
  // canceled composition carries no committed data and only republishes the
  // resolved spans, so it records nothing.
  const reconcileComposition = (text: string, composition: Composition) => {
    compositionRef.current = null;
    composeGenRef.current++;
    // A canceled composition (empty committed data) that leaves the text exactly
    // as its captured base proved no edit: the browser restored the base, so its
    // spans keep the base's certified ids and it adds no journal entry. This is
    // read from the committed mark, never from matching text, so a committed
    // equal-text replacement still records.
    if (!composition.committed && text === composition.base.text) {
      publish({ text, live: composition.base.live });
      return;
    }
    const live = compositionLive(text, composition);
    if (
      text !== composition.base.text ||
      !sameLive(live, composition.base.live) ||
      composition.committed
    ) {
      record({ text, live });
    } else {
      publish({ text, live });
    }
  };

  const applyChange = (
    text: string,
    start: number,
    end: number,
    inputType: string,
    native: Event
  ) => {
    if (consumedRef.current === native) return;
    consumedRef.current = native;
    const current = snapRef.current;

    // Composition, active or ending, is one base-anchored replacement.
    const composing = compositionRef.current;
    if (composing) {
      if (composing.ending) reconcileComposition(text, composing);
      else publish({ text, live: compositionLive(text, composing) });
      return;
    }

    const pending = pendingRef.current;
    pendingRef.current = null;
    const type = inputType || pending?.inputType || '';

    if (type in HISTORY_TYPES) {
      restoreHistory(text, type);
      return;
    }

    // No verified capture: keep the browser's text and certify nothing. An
    // actual committed input is still a native history boundary, so the journal
    // records the state — even when the visible text is unchanged — and its
    // uncertain identities become plain.
    if (
      !pending ||
      pending.composing ||
      pending.value !== current.text ||
      pending.gen !== pendingGenRef.current
    ) {
      record({ text, live: [] });
      return;
    }

    const splice = resolveSplice(
      current.text,
      text,
      pending,
      { start, end },
      type
    );
    if (!splice) {
      record({ text, live: [] });
      return;
    }

    // A verified replacement that changes neither the text nor the live spans is
    // still a committed equal-text native transaction, so it needs a journal
    // entry for a later undo to reach.
    const live = transition(text, current.live, splice, labelAt);
    record({ text, live });
  };

  // Native listeners, not React synthetics: the capture needs the real
  // beforeinput selection and inputType, and the paired input must still be
  // seen when React's value tracker would swallow an unchanged value.
  const applyRef = useRef(applyChange);
  applyRef.current = applyChange;
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    const onBeforeInput = (event: InputEvent) => {
      const gen = ++pendingGenRef.current;
      pendingRef.current = {
        value: el.value,
        start: el.selectionStart ?? 0,
        end: el.selectionEnd ?? 0,
        inputType: event.inputType ?? '',
        composing: event.isComposing,
        gen,
      };
      // The paired input runs before this task's timer, so a capture the
      // browser cancels is dropped instead of being reused by a later action.
      setTimeout(() => {
        const pending = pendingRef.current;
        if (pending && pending.gen === gen) pendingRef.current = null;
      }, 0);
    };
    const onInput = (event: InputEvent) => {
      applyRef.current(
        el.value,
        el.selectionStart ?? 0,
        el.selectionEnd ?? 0,
        event.inputType ?? '',
        event
      );
    };
    const onCompositionStart = () => {
      compositionRef.current = {
        base: snapRef.current,
        start: el.selectionStart ?? 0,
        end: el.selectionEnd ?? 0,
        ending: false,
        committed: false,
        gen: ++composeGenRef.current,
      };
    };
    const onCompositionEnd = (event: CompositionEvent) => {
      const composition = compositionRef.current;
      if (!composition) return;
      // Keep the session in `ending` until the final input or the deferred
      // reconciliation settles it once, so a browser that ends composition
      // before the final input still resolves against the base anchor. The
      // deferred callback alone is not a guarantee of every legacy ordering.
      // Non-empty committed data is the native mark that this composition
      // inserted text; a canceled composition ends with empty data.
      composition.committed = (event.data ?? '').length > 0;
      composition.ending = true;
      const gen = ++composeGenRef.current;
      setTimeout(() => {
        if (
          compositionRef.current === composition &&
          composeGenRef.current === gen
        )
          reconcileComposition(el.value, composition);
      }, 0);
    };
    el.addEventListener('beforeinput', onBeforeInput);
    el.addEventListener('input', onInput);
    el.addEventListener('compositionstart', onCompositionStart);
    el.addEventListener('compositionend', onCompositionEnd);
    return () => {
      el.removeEventListener('beforeinput', onBeforeInput);
      el.removeEventListener('input', onInput);
      el.removeEventListener('compositionstart', onCompositionStart);
      el.removeEventListener('compositionend', onCompositionEnd);
    };
  }, []);

  const reactInput = (event: FormEvent<HTMLTextAreaElement>) => {
    const el = event.currentTarget;
    applyRef.current(
      el.value,
      el.selectionStart ?? 0,
      el.selectionEnd ?? 0,
      (event.nativeEvent as InputEvent).inputType ?? '',
      event.nativeEvent
    );
  };

  const next = encodeSnapshot(snap, occurrences).trim();

  const save = async () => {
    if (saving || next === '') return;
    // Unchanged text closes and sends nothing.
    if (next === body) {
      onCancel();
      return;
    }
    setSaving(true);
    try {
      await onSave(next);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-composer-box
      className='w-full rounded-lg border border-input bg-background has-[textarea:focus-visible]:border-primary has-[textarea:focus-visible]:ring-1 has-[textarea:focus-visible]:ring-primary'
      onClick={event => event.stopPropagation()}
      // Escape is handled here, not on the text area: a focused Cancel or Save
      // must also cancel without the window listener minimizing the chat.
      // While a save runs, Escape keeps the editor open (Cancel is disabled).
      onKeyDown={event => {
        if (event.key === 'Escape') {
          event.preventDefault();
          if (!saving) onCancel();
        }
      }}
    >
      <Textarea
        ref={textareaRef}
        textareaSize={touch ? 'autoGrowLg' : 'autoGrow'}
        rows={1}
        autoFocus
        aria-label='Edit message'
        value={snap.text}
        onChange={reactInput}
        onInput={reactInput}
        onKeyDown={event => {
          // Enter saves except during composition or an IME commit key. The
          // session stays alive through the legacy compositionend-before-Enter
          // ordering, so the Enter that commits IME text does not also save;
          // native keyCode 229 is the compatibility signal for a commit keydown
          // that arrives with isComposing already false.
          if (
            event.key === 'Enter' &&
            !event.shiftKey &&
            !event.nativeEvent.isComposing &&
            event.nativeEvent.keyCode !== 229 &&
            compositionRef.current === null
          ) {
            event.preventDefault();
            void save();
          }
        }}
      />
      <div className='flex justify-end gap-2 px-2 pb-2'>
        <Button
          type='button'
          variant='ghost'
          size={touch ? 'touch' : 'sm'}
          disabled={saving}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          type='button'
          size={touch ? 'touch' : 'sm'}
          disabled={saving || next === ''}
          onClick={() => void save()}
        >
          Save
        </Button>
      </div>
    </div>
  );
}
